import React, { useState, useEffect } from 'react';
import { FaTimes, FaMinus, FaPlus } from 'react-icons/fa';

const ItemCustomizationBottomSheet = ({
  isOpen,
  onClose,
  dish, // The main dish object
  variants = [], // Array of variants (empty for basic dishes)
  onAddToCart, // Callback to add to cart with selected variant and add-ons
  isLoading = false,
  defaultPriciestVariant = false
}) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedAddOns, setSelectedAddOns] = useState({});

  const getDefaultVariant = () => {
    if (!variants || variants.length === 0) return null;

    if (!defaultPriciestVariant) {
      return variants[0];
    }

    // Prefer available variants if the field exists
    const candidates = variants.filter((v) => v?.available !== false);
    const list = candidates.length > 0 ? candidates : variants;

    return list.reduce((max, current) => {
      const maxPrice = max?.price ?? 0;
      const currentPrice = current?.price ?? 0;
      return currentPrice > maxPrice ? current : max;
    }, list[0]);
  };

  // Reset state when bottom sheet opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedVariant(getDefaultVariant());
      setQuantity(1);
      setSelectedAddOns({});
    }
  }, [isOpen, variants, defaultPriciestVariant]);

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    // Reset add-ons when variant changes
    setSelectedAddOns({});
  };

  const handleQuantityChange = (increment) => {
    const newQuantity = quantity + increment;
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const handleAddOnChange = (groupName, itemName, checked, maxSelectionCount) => {
    setSelectedAddOns(prev => {
      const current = prev[groupName] || [];
      let updated;
      
      if (checked) {
        if (maxSelectionCount === 1) {
          // Radio button behavior
          updated = [itemName];
        } else {
          // Checkbox behavior
          if (current.length < maxSelectionCount) {
            updated = [...current, itemName];
          } else {
            updated = current; // Don't add if max reached
          }
        }
      } else {
        updated = current.filter(item => item !== itemName);
      }
      
      return {
        ...prev,
        [groupName]: updated
      };
    });
  };

  const handleAddToCart = () => {
    const addOnItems = [];
    
    // Convert selected add-ons to the required format
    Object.entries(selectedAddOns).forEach(([groupName, selectedItems]) => {
      const addOnGroups = getCurrentAddOnGroups();
      const group = addOnGroups.find(g => g.name === groupName);
      if (group) {
        selectedItems.forEach(itemName => {
          const item = group.items.find(i => i.name === itemName);
          if (item) {
            addOnItems.push({
              addOnItemId: item.addOnItemId,
              name: item.name,
              price: item.price,
              available: item.available
            });
          }
        });
      }
    });

    onAddToCart(selectedVariant, quantity, addOnItems);
  };

  const getCurrentAddOnGroups = () => {
    if (selectedVariant) {
      // Return variant add-ons if variant is selected
      return selectedVariant.addOnGroups || [];
    } else {
      // Return dish add-ons for basic dishes
      return dish.addOnGroups || [];
    }
  };

  const formatPrice = (price) => {
    return `₹${(price / 100).toFixed(2)}`;
  };

  const calculateTotalPrice = () => {
    let basePrice = selectedVariant ? selectedVariant.price : (dish?.price || 0);
    let addOnPrice = 0;

    Object.entries(selectedAddOns).forEach(([groupName, selectedItems]) => {
      const addOnGroups = getCurrentAddOnGroups();
      const group = addOnGroups.find(g => g.name === groupName);
      if (group) {
        selectedItems.forEach(itemName => {
          const item = group.items.find(i => i.name === itemName);
          if (item) {
            addOnPrice += item.price;
          }
        });
      }
    });

    return (basePrice + addOnPrice) * quantity;
  };

  if (!isOpen || !dish) return null;

  const currentAddOnGroups = getCurrentAddOnGroups();
  const hasVariants = variants.length > 0;
  const hasAddOns = currentAddOnGroups.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-end">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Bottom Sheet */}
      <div className="relative w-full bg-white rounded-t-3xl max-h-[80vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
              {/* Placeholder for dish image */}
              <span className="text-gray-500 text-xs">🍽️</span>
            </div>
            <div>
              <h3 className="font-semibold text-lg">{dish?.name || ''}</h3>
              <p className="text-gray-600 text-sm">
                {hasVariants ? 'Choose your variant' : 'Customize your order'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <FaTimes className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4">
            {/* Variants Section - Only show if variants exist */}
            {hasVariants && (
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-3">Choose your variant</h4>
                <div className="space-y-3">
                  {variants.map((variant) => (
                    <div
                      key={variant.variationId}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedVariant?.variationId === variant.variationId
                          ? 'border-[#FBC02D] bg-yellow-50'
                          : 'border-gray-200 hover:border-gray-300'
                      } ${!variant.available ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => variant.available && handleVariantSelect(variant)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{variant.name}</h4>
                          <p className="text-[#171725] font-semibold">{formatPrice(variant.price)}</p>
                          {!variant.available && (
                            <p className="text-red-500 text-sm">Currently Unavailable</p>
                          )}
                          {variant.addOnGroups && variant.addOnGroups.length > 0 && (
                            <p className="text-blue-600 text-xs">Customizable options available</p>
                          )}
                        </div>
                        <div className="ml-4">
                          <input
                            type="radio"
                            name="variant-selection"
                            checked={selectedVariant?.variationId === variant.variationId}
                            disabled={!variant.available}
                            onChange={() => variant.available && handleVariantSelect(variant)}
                            className="w-4 h-4 text-[#FBC02D]"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add-ons Section - Only show if add-ons exist */}
            {hasAddOns && (
              <div className="mb-6">
                <h4 className="font-semibold text-lg mb-3">Customize your order</h4>
                <div className="space-y-6">
                  {currentAddOnGroups.map((group) => (
                    <div key={group.name} className="border-b border-gray-100 pb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-lg">{group.name}</h4>
                        <span className="text-sm text-gray-500">
                          {group.maxSelectionCount === 1 
                            ? 'Select 1' 
                            : `Select up to ${group.maxSelectionCount}`
                          }
                        </span>
                      </div>
                      
                      <div className="space-y-3">
                        {group.items.map((item) => (
                          <label 
                            key={item.name} 
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              selectedAddOns[group.name]?.includes(item.name)
                                ? 'border-[#FBC02D] bg-yellow-50'
                                : 'border-gray-200 hover:border-gray-300'
                            } ${!item.available ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <div className="flex items-center space-x-3">
                              <input
                                type={group.maxSelectionCount === 1 ? 'radio' : 'checkbox'}
                                name={`addon-${group.name}`}
                                value={item.name}
                                checked={selectedAddOns[group.name]?.includes(item.name) || false}
                                disabled={
                                  !item.available || 
                                  (group.maxSelectionCount !== 1 && 
                                   selectedAddOns[group.name]?.length >= group.maxSelectionCount && 
                                   !selectedAddOns[group.name]?.includes(item.name))
                                }
                                onChange={(e) => handleAddOnChange(group.name, item.name, e.target.checked, group.maxSelectionCount)}
                                className="w-4 h-4 text-[#FBC02D]"
                              />
                              <div>
                                <span className="font-medium">{item.name}</span>
                                {!item.available && (
                                  <p className="text-red-500 text-xs">Currently Unavailable</p>
                                )}
                              </div>
                            </div>
                            {item.price > 0 && (
                              <span className="text-gray-600 font-medium">
                                +{formatPrice(item.price)}
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fixed Bottom Section */}
        <div className="border-t border-gray-200 bg-white flex-shrink-0 p-4">
          <div className="flex items-center justify-between">
            {/* Quantity Controls */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
                className={`p-2 rounded-full ${
                  quantity <= 1 
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                <FaMinus className="w-3 h-3" />
              </button>
              <span className="text-xl font-semibold min-w-[2rem] text-center">
                {quantity}
              </span>
              <button
                onClick={() => handleQuantityChange(1)}
                className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300"
              >
                <FaPlus className="w-3 h-3" />
              </button>
            </div>

            {/* Add to Cart Button with Price */}
            <button
              onClick={handleAddToCart}
              disabled={isLoading || (hasVariants && !selectedVariant)}
              className={`px-6 py-3 rounded-lg font-semibold text-white transition-all flex items-center space-x-2 ${
                isLoading || (hasVariants && !selectedVariant)
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-[#FBC02D] hover:bg-[#F9A825]'
              }`}
            >
              <span>{isLoading ? 'Adding...' : 'Add Item'}</span>
              <span className="text-sm opacity-90">|</span>
              <span className="text-sm">{formatPrice(calculateTotalPrice())}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemCustomizationBottomSheet; 